"use client";

import { useMemo, useState } from "react";
import {
  CopyButton,
  DataTable,
  Notice,
  PillGroup,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  BUTTON_SMALL,
  HINT,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { useWordList } from "@/components/tools/word-games/use-word-list";
import { WordListStatus } from "@/components/tools/word-games/word-list-status";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  ABSENT,
  type Clue,
  cleanGuess,
  DEFAULT_LENGTH,
  filterCandidates,
  MAX_LENGTH,
  MAX_SPLIT_CANDIDATES,
  type Mark,
  MIN_LENGTH,
  rankByLetterFrequency,
  rankBySplit,
  scoreGuess,
  summarize,
} from "@/lib/tool-kit/word-games/wordle";

/** English strings. A translated content file overrides these through `ui`. */
export const WORDLE_SOLVER_STRINGS = {
  lengthLabel: "Word length",
  lengthOption: "one:{count} letter|other:{count} letters",
  guessLabel: "Guess {number}",
  guessPlaceholder: "raise",
  tilesHint:
    "Type each guess, then press its tiles to match the colors the game showed: gray, yellow, green.",
  stateNames: ["Gray", "Yellow", "Green"],
  tileLabel: "Letter {letter}, position {position}, {state}. Press to change.",
  addGuess: "Add a guess",
  removeGuess: "Remove last guess",
  clear: "Clear all",
  tryExample: "Try an example",
  emptyPrompt:
    "Type your first guess and set its colors. Until then, these are the best words to open with.",
  loading: "Loading the word list (once per visit)…",
  loadError:
    "Could not load the word list. Check your connection and try again.",
  retry: "Try again",
  sortLabel: "Rank suggestions by",
  sorts: { frequency: "Letter frequency", split: "Best split" },
  splitLimit:
    "Best split compares every word with every other, so it switches on at {max} words or fewer.",
  heading: "Words that fit your clues",
  candidateCount: "one:{count} word fits|other:{count} words fit",
  knownPattern: "Fixed letters",
  knownContains: "Must contain",
  knownExcluded: "Ruled out",
  none: "none yet",
  noMatch:
    "No word in the list fits these clues. Check each tile's color and each letter for a slip — or the answer may be a word this list does not have.",
  suggestionsHeading: "Best next guesses",
  columns: {
    rank: "#",
    word: "Word",
    score: "Coverage score",
    expected: "Words left on average",
    worst: "Worst case",
  },
  showMore: "Show more suggestions",
  allHeading: "Every word that fits",
  showAll: "Show all {count}",
  copyAll: "Copy all",
  copied: "Copied!",
  sourceNote:
    "Words come from ENABLE, a public-domain word-game list. It is not the list the game uses, so the answer may be missing and rare words may appear — favor the familiar ones.",
} as const;

type Strings = typeof WORDLE_SOLVER_STRINGS;
type SortMode = "frequency" | "split";

interface Row {
  guess: string;
  marks: Mark[];
}

const RE_NOT_LETTER = /[^a-z]/g;
const EXAMPLE_ANSWER = "light";
const EXAMPLE_GUESSES = ["raise", "clout"];
const MAX_ROWS = 8;
const PREVIEW_SUGGESTIONS = 10;
const MORE_SUGGESTIONS = 50;
const PREVIEW_WORDS = 200;
const LENGTHS = Array.from(
  { length: MAX_LENGTH - MIN_LENGTH + 1 },
  (_, index) => MIN_LENGTH + index
);
const TILE_CLASSES = [
  "border-border bg-muted text-foreground",
  "border-amber-500 bg-amber-300 text-amber-950",
  "border-emerald-700 bg-emerald-500 text-white",
];
const TILE_GLYPHS = ["", "•", "✓"];

function emptyRow(length: number): Row {
  return { guess: "", marks: new Array<Mark>(length).fill(ABSENT) };
}

function GuessRow({
  index,
  row,
  length,
  strings,
  onGuess,
  onCycle,
}: {
  index: number;
  row: Row;
  length: number;
  strings: Strings;
  onGuess: (value: string) => void;
  onCycle: (position: number) => void;
}) {
  const fmt = useToolNumber();
  const slots = Array.from({ length }, (_, position) => ({
    position,
    letter: row.guess[position],
  }));
  return (
    <div className="grid gap-3 sm:grid-cols-[minmax(0,11rem)_1fr] sm:items-end">
      <TextField
        id={`wordle-guess-${index}`}
        label={t(strings.guessLabel, { number: fmt(index + 1) })}
        mono
        onChange={onGuess}
        placeholder={strings.guessPlaceholder}
        value={row.guess}
      />
      <div className="flex gap-2">
        {slots.map(({ position, letter }) =>
          letter ? (
            <button
              aria-label={t(strings.tileLabel, {
                letter: letter.toUpperCase(),
                position: fmt(position + 1),
                state: strings.stateNames[row.marks[position]],
              })}
              className={`relative flex h-11 w-11 items-center justify-center rounded-md border-2 font-bold text-lg uppercase ${TILE_CLASSES[row.marks[position]]}`}
              key={`tile-${position}`}
              onClick={() => onCycle(position)}
              type="button"
            >
              {letter}
              <span
                aria-hidden="true"
                className="absolute end-1 top-0 text-[10px] leading-4"
              >
                {TILE_GLYPHS[row.marks[position]]}
              </span>
            </button>
          ) : (
            <span
              aria-hidden="true"
              className="h-11 w-11 rounded-md border-2 border-border border-dashed"
              key={`blank-${position}`}
            />
          )
        )}
      </div>
    </div>
  );
}

function Known({
  length,
  clues,
  strings,
}: {
  length: number;
  clues: readonly Clue[];
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const known = summarize(length, clues);
  const pattern = known.pattern.map((letter) => letter || "_").join(" ");
  const contains = known.contains
    .map(({ letter, atLeast }) =>
      atLeast > 1 ? `${letter}×${fmt(atLeast)}` : letter
    )
    .join(" ");
  return (
    <dl className="grid gap-x-6 gap-y-1 text-sm sm:grid-cols-[auto_1fr]">
      <dt className="text-muted-foreground">{strings.knownPattern}</dt>
      <dd className="font-medium font-mono uppercase">{pattern}</dd>
      <dt className="text-muted-foreground">{strings.knownContains}</dt>
      <dd className="font-medium font-mono uppercase">
        {contains || strings.none}
      </dd>
      <dt className="text-muted-foreground">{strings.knownExcluded}</dt>
      <dd className="font-medium font-mono uppercase">
        {known.excluded.join(" ") || strings.none}
      </dd>
    </dl>
  );
}

function Suggestions({
  candidates,
  sort,
  strings,
}: {
  candidates: readonly string[];
  sort: SortMode;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const [more, setMore] = useState(false);
  const rows = useMemo(() => {
    if (sort === "split") {
      return rankBySplit(candidates)
        .slice(0, MORE_SUGGESTIONS)
        .map((entry, index) => [
          fmt(index + 1),
          entry.word,
          fmt(entry.expected, { maximumFractionDigits: 2 }),
          fmt(entry.worst),
        ]);
    }
    return rankByLetterFrequency(candidates)
      .slice(0, MORE_SUGGESTIONS)
      .map((entry, index) => [fmt(index + 1), entry.word, fmt(entry.score)]);
  }, [candidates, sort, fmt]);
  const head =
    sort === "split"
      ? [
          strings.columns.rank,
          strings.columns.word,
          strings.columns.expected,
          strings.columns.worst,
        ]
      : [strings.columns.rank, strings.columns.word, strings.columns.score];
  return (
    <section className="space-y-3">
      <h3 className={WIDGET_H3}>{strings.suggestionsHeading}</h3>
      <DataTable
        head={head}
        rows={rows.slice(0, more ? MORE_SUGGESTIONS : PREVIEW_SUGGESTIONS)}
      />
      {!more && rows.length > PREVIEW_SUGGESTIONS ? (
        <button
          className={BUTTON_SMALL}
          onClick={() => setMore(true)}
          type="button"
        >
          {strings.showMore}
        </button>
      ) : null}
    </section>
  );
}

function AllWords({
  candidates,
  strings,
}: {
  candidates: readonly string[];
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const [expanded, setExpanded] = useState(false);
  const shown = expanded ? candidates : candidates.slice(0, PREVIEW_WORDS);
  return (
    <section className="space-y-2">
      <h3 className={WIDGET_H3}>{strings.allHeading}</h3>
      <ul className="flex flex-wrap gap-2">
        {shown.map((word) => (
          <li
            className="rounded-md border border-border bg-card px-2.5 py-1 font-mono text-foreground text-sm"
            key={word}
          >
            {word}
          </li>
        ))}
      </ul>
      {candidates.length > shown.length ? (
        <button
          className={BUTTON_SMALL}
          onClick={() => setExpanded(true)}
          type="button"
        >
          {t(strings.showAll, { count: fmt(candidates.length) })}
        </button>
      ) : null}
    </section>
  );
}

export default function WordleSolverWidget() {
  const strings = useToolUi(WORDLE_SOLVER_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [length, setLength] = useState(DEFAULT_LENGTH);
  const [rows, setRows] = useState<Row[]>([emptyRow(DEFAULT_LENGTH)]);
  const [sort, setSort] = useState<SortMode>("frequency");
  const typed = rows.some((row) => row.guess.length > 0);
  const { words, status, retry } = useWordList(typed);

  const clues: Clue[] = rows.flatMap((row) => {
    const guess = cleanGuess(row.guess);
    return guess && guess.length === length
      ? [{ guess, marks: row.marks }]
      : [];
  });
  const clueKey = clues
    .map((clue) => `${clue.guess}:${clue.marks.join("")}`)
    .join("|");
  // biome-ignore lint/correctness/useExhaustiveDependencies: clueKey stands for clues
  const candidates = useMemo(
    () => (words ? filterCandidates(words, length, clues) : null),
    [words, length, clueKey]
  );
  const splitAvailable =
    candidates !== null && candidates.length <= MAX_SPLIT_CANDIDATES;
  const effectiveSort = splitAvailable ? sort : "frequency";

  function changeLength(value: number) {
    setLength(value);
    setRows([emptyRow(value)]);
  }

  function setGuess(index: number, value: string) {
    const clean = value
      .toLowerCase()
      .replace(RE_NOT_LETTER, "")
      .slice(0, length);
    setRows((current) =>
      current.map((row, at) => (at === index ? { ...row, guess: clean } : row))
    );
  }

  function cycle(index: number, position: number) {
    setRows((current) =>
      current.map((row, at) => {
        if (at !== index) {
          return row;
        }
        const marks = [...row.marks];
        marks[position] = ((marks[position] + 1) % 3) as Mark;
        return { ...row, marks };
      })
    );
  }

  function loadExample() {
    setLength(DEFAULT_LENGTH);
    setRows(
      EXAMPLE_GUESSES.map((guess) => ({
        guess,
        marks: scoreGuess(guess, EXAMPLE_ANSWER),
      }))
    );
  }

  return (
    <>
      <div className="grid gap-4 sm:grid-cols-[1fr_2fr]">
        <SelectField
          id="wordle-length"
          label={strings.lengthLabel}
          onChange={(value) => changeLength(Number(value))}
          options={LENGTHS.map((count) => ({
            value: String(count),
            label: tPlural(strings.lengthOption, locale, count, {
              count: fmt(count),
            }),
          }))}
          value={String(length)}
        />
        <p className={`${HINT} sm:self-end`}>{strings.tilesHint}</p>
      </div>
      <div className="space-y-4">
        {rows.map((row, index) => (
          <GuessRow
            index={index}
            // biome-ignore lint/suspicious/noArrayIndexKey: rows have no identity beyond position
            key={index}
            length={length}
            onCycle={(position) => cycle(index, position)}
            onGuess={(value) => setGuess(index, value)}
            row={row}
            strings={strings}
          />
        ))}
      </div>
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_SECONDARY}
          disabled={rows.length >= MAX_ROWS}
          onClick={() => setRows((current) => [...current, emptyRow(length)])}
          type="button"
        >
          {strings.addGuess}
        </button>
        <button
          className={BUTTON_SECONDARY}
          disabled={rows.length <= 1}
          onClick={() => setRows((current) => current.slice(0, -1))}
          type="button"
        >
          {strings.removeGuess}
        </button>
        <button
          className={BUTTON_SECONDARY}
          onClick={() => setRows([emptyRow(length)])}
          type="button"
        >
          {strings.clear}
        </button>
        <button
          className={BUTTON_SECONDARY}
          onClick={loadExample}
          type="button"
        >
          {strings.tryExample}
        </button>
      </div>
      <WordListStatus
        error={strings.loadError}
        loading={strings.loading}
        onRetry={retry}
        retry={strings.retry}
        status={status}
      />
      {candidates ? (
        <div aria-live="polite" className="space-y-5">
          {clues.length === 0 ? <Notice>{strings.emptyPrompt}</Notice> : null}
          {candidates.length === 0 ? (
            <Notice>{strings.noMatch}</Notice>
          ) : (
            <>
              <div className="flex flex-wrap items-end justify-between gap-3">
                <div className="space-y-1">
                  <h2 className={WIDGET_H2}>{strings.heading}</h2>
                  <p className={HINT}>
                    {tPlural(
                      strings.candidateCount,
                      locale,
                      candidates.length,
                      {
                        count: fmt(candidates.length),
                      }
                    )}
                  </p>
                </div>
                <CopyButton
                  copiedLabel={strings.copied}
                  label={strings.copyAll}
                  small
                  text={candidates.join("\n")}
                />
              </div>
              {clues.length > 0 ? (
                <Known clues={clues} length={length} strings={strings} />
              ) : null}
              {splitAvailable ? (
                <PillGroup
                  label={strings.sortLabel}
                  name="wordle-sort"
                  onChange={setSort}
                  options={[
                    { value: "frequency", label: strings.sorts.frequency },
                    { value: "split", label: strings.sorts.split },
                  ]}
                  value={effectiveSort}
                />
              ) : (
                <p className={HINT}>
                  {t(strings.splitLimit, { max: fmt(MAX_SPLIT_CANDIDATES) })}
                </p>
              )}
              <Suggestions
                candidates={candidates}
                sort={effectiveSort}
                strings={strings}
              />
              <AllWords candidates={candidates} strings={strings} />
            </>
          )}
        </div>
      ) : null}
      <p className={HINT}>{strings.sourceNote}</p>
    </>
  );
}
